/**
 * Cookie Consent Banner
 * GDPR-compliant cookie consent with essential/all options
 * Shows on first visit, respects Do Not Track
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie, Shield } from 'lucide-react';
import { LEGAL_URLS } from '@/constants/legalVersions';

const CONSENT_STORAGE_KEY = 'finlist_cookie_consent';
const CONSENT_VERSION = 'v1.0';

/**
 * Get stored cookie consent
 */
function getStoredConsent() {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    // Check version match
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Store cookie consent
 */
function storeConsent(level) {
  const consent = {
    level, // 'essential' | 'all'
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    doNotTrack: navigator.doNotTrack === '1'
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
  return consent;
}

/**
 * Check if browser has Do Not Track enabled
 */
function hasDoNotTrack() {
  return navigator.doNotTrack === '1' || 
         navigator.doNotTrack === 'yes' ||
         window.doNotTrack === '1';
}

export default function CookieConsentBanner() {
  // FINLIST FIX: Check consent immediately and use ref to prevent race conditions
  // This ensures the banner never shows again once consent is given
  const initialConsent = getStoredConsent();
  const hasConsentedRef = useRef(!!initialConsent);
  const [showBanner, setShowBanner] = useState(false);
  const [consent, setConsent] = useState(initialConsent);
  
  // Track if we've already shown/hidden the banner to prevent re-triggering
  const [hasInitialized, setHasInitialized] = useState(!!initialConsent);

  useEffect(() => {
    // FINLIST FIX: Always re-check localStorage on mount to catch any consent
    // This handles cases where consent was given in another tab
    const storedConsent = getStoredConsent();
    if (storedConsent) {
      hasConsentedRef.current = true;
      setConsent(storedConsent);
      setShowBanner(false);
      setHasInitialized(true);
      return;
    }
    
    // Only show banner if we haven't initialized and there's no consent
    if (hasInitialized || hasConsentedRef.current) return;
    
    // Show banner after a short delay for better UX
    const timer = setTimeout(() => {
      // Final check before showing - user might have consented during delay
      const finalCheck = getStoredConsent();
      if (finalCheck) {
        hasConsentedRef.current = true;
        setConsent(finalCheck);
        setShowBanner(false);
      } else {
        setShowBanner(true);
      }
      setHasInitialized(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, [hasInitialized]);

  const handleAcceptEssential = () => {
    hasConsentedRef.current = true;
    const newConsent = storeConsent('essential');
    setConsent(newConsent);
    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    hasConsentedRef.current = true;
    const newConsent = storeConsent('all');
    setConsent(newConsent);
    setShowBanner(false);
  };

  const handleDismiss = () => {
    // Dismissing without choice = essential only (privacy-first)
    handleAcceptEssential();
  };

  // Don't render if consent already given
  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 pointer-events-none"
      >
        <div className="max-w-2xl mx-auto pointer-events-auto">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-500/10 rounded-xl">
                  <Cookie className="w-5 h-5 text-brand-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Cookie Preferences
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Your privacy matters to us
                  </p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-5 py-4">
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                We use cookies to provide essential functionality and improve your experience. 
                {hasDoNotTrack() && (
                  <span className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    We detected your Do Not Track preference and will respect it.
                  </span>
                )}
              </p>

              {/* Do Not Track Notice */}
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Learn more in our{' '}
                <Link 
                  to={LEGAL_URLS.cookies}
                  className="text-brand-600 hover:underline"
                >
                  Cookies Policy
                </Link>
                {' '}and{' '}
                <Link 
                  to={LEGAL_URLS.privacy}
                  className="text-brand-600 hover:underline"
                >
                  Privacy Policy
                </Link>
                .
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleAcceptEssential}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                >
                  Essential Only
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-4 py-3 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 transition-colors shadow-lg shadow-brand-500/20"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Hook to check cookie consent level
 * @returns {'essential' | 'all' | null}
 */
export function useCookieConsent() {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const stored = getStoredConsent();
    setConsent(stored?.level || null);
  }, []);

  return consent;
}

/**
 * Check if analytics cookies are allowed
 */
export function canUseAnalyticsCookies() {
  const stored = getStoredConsent();
  if (!stored) return false;
  if (hasDoNotTrack()) return false;
  return stored.level === 'all';
}
